import React from 'react';
import { Upload, Wifi, Info, ChevronRight} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';

/**
 * Empty-state visuel « classe vide ». L'import ne se fait plus ici : la seule
 * porte est le wizard d'import établissement (délégué/chef — spec GAR §0.7),
 * vers lequel le CTA renvoie quand le rôle l'autorise.
 */
const EmptyClassroom = ({
  matiere,
  classe,
  role = 'chef', // 'chef' ou 'prof'
  showActions = true
}) => {
  const navigate = useNavigate();
  const { etablissementData, user } = useUser();
  const estDelegue = !!etablissementData?.professeurs?.find(p => p.id === user?.id)?.delegue_affectation;
  const peutImporter = role === 'chef' || estDelegue;

  // Composant Chaise
const Chair = ({ className = "", style = {} }) => (
  <svg className={className} style={style} viewBox="0 0 60 80" fill="none">
    {/* Dossier */}
    <rect x="8" y="0" width="44" height="28" rx="6" fill="currentColor" opacity="0.4"/>
    {/* Assise */}
    <rect x="4" y="28" width="52" height="8" rx="3" fill="currentColor"/>
    {/* Pieds */}
    <rect x="10" y="36" width="6" height="40" rx="2" fill="currentColor" opacity="0.8"/>
    <rect x="44" y="36" width="6" height="40" rx="2" fill="currentColor" opacity="0.8"/>
    {/* Barre transversale */}
    <rect x="10" y="56" width="40" height="4" rx="2" fill="currentColor" opacity="0.6"/>
  </svg>
);

// Composant Bureau/Table
const Desk = ({ className = "", style = {} }) => (
  <svg className={className} style={style} viewBox="0 0 200 60" fill="none">
    {/* Plateau */}
    <rect x="0" y="0" width="200" height="12" rx="4" fill="currentColor"/>
    {/* Pieds */}
    <rect x="15" y="12" width="8" height="45" rx="2" fill="currentColor" opacity="0.7"/>
    <rect x="177" y="12" width="8" height="45" rx="2" fill="currentColor" opacity="0.7"/>
    {/* Barre de support */}
    <rect x="15" y="40" width="170" height="4" rx="2" fill="currentColor" opacity="0.5"/>
  </svg>
);

// Composant Livre
const Book = ({ className = "", color = "#8b5cf6", style = {} }) => (
  <svg className={className} style={style} viewBox="0 0 40 50" fill="none">
    <rect x="2" y="0" width="36" height="50" rx="3" fill={color}/>
    <rect x="6" y="4" width="28" height="4" rx="1" fill="white" opacity="0.3"/>
    <rect x="6" y="12" width="20" height="2" rx="1" fill="white" opacity="0.2"/>
    <rect x="6" y="18" width="24" height="2" rx="1" fill="white" opacity="0.2"/>
    {/* Reliure */}
    <rect x="0" y="0" width="4" height="50" rx="2" fill={color} opacity="0.7"/>
  </svg>
);

// Composant Crayon
const Pencil = ({ className = "", style = {} }) => (
  <svg className={className} style={style} viewBox="0 0 80 16" fill="none">
    <rect x="0" y="2" width="60" height="12" rx="2" fill="#fbbf24"/>
    <rect x="0" y="2" width="60" height="4" fill="#fcd34d"/>
    <polygon points="60,2 75,8 60,14" fill="#fef3c7"/>
    <polygon points="75,8 80,8 75,8" fill="#374151"/>
    <rect x="52" y="2" width="8" height="12" fill="#f472b6" opacity="0.8"/>
  </svg>
);

// Composant Point d'interrogation animé
const QuestionMark = ({ className = "", size = "text-4xl", delay = "0s", duration = "2s" }) => (
  <div 
    className={`font-bold ${size} ${className} animate-bounce`} 
    style={{ animationDuration: duration, animationDelay: delay }}
  >
    ?
  </div>
);
  return (
    <>
    <div className="min-h-[500px] flex items-center justify-center p-4 sm:p-8">
      <div className="bg-white rounded-3xl shadow-xl p-4 sm:p-6 md:p-8 lg:p-10 w-full text-center max-w-5xl">
        {/* Grande illustration de classe vide */}
        <div className="relative w-full h-48 sm:h-64 md:h-72 lg:h-80 mx-auto mb-6 sm:mb-8 lg:mb-10">
          
          {/* Fond de salle de classe */}
          <div className="absolute inset-x-0 bottom-0 h-4 bg-gradient-to-r from-slate-400 via-slate-300 to-slate-400 rounded-full opacity-60" />
          
          {/* Tableau noir au fond */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 sm:w-56 md:w-64 lg:w-72 h-12 sm:h-16 md:h-18 lg:h-20 bg-gradient-to-b from-emerald-900 to-emerald-950 rounded-lg shadow-xl border-2 sm:border-3 lg:border-4 border-amber-700">
            <div className="absolute inset-1 sm:inset-2 border border-amber-600/30 rounded" />
            {/* Texte craie */}
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-green-200/40 font-mono text-xs sm:text-sm">En attente...</span>
            </div>
          </div>
          
          {/* Bureau du prof */}
          <Desk
            className="absolute bottom-12 sm:bottom-16 md:bottom-20 left-1/2 -translate-x-1/2 w-32 sm:w-48 md:w-56 lg:w-64 h-8 sm:h-12 md:h-14 lg:h-16 text-amber-600"
          />

          {/* Livres sur le bureau - Hidden on mobile */}
          <div className="hidden sm:block absolute bottom-16 sm:bottom-20 md:bottom-28 left-1/2 translate-x-4 sm:translate-x-6 md:translate-x-8">
            <Book className="w-6 sm:w-8 md:w-10 h-8 sm:h-10 md:h-12 transform -rotate-6" color="#8b5cf6" />
          </div>
          <div className="hidden md:block absolute bottom-16 sm:bottom-20 md:bottom-28 left-1/2 translate-x-10 sm:translate-x-14 md:translate-x-16">
            <Book className="w-5 sm:w-6 md:w-8 h-6 sm:h-8 md:h-10 transform rotate-3" color="#3b82f6" />
          </div>

          {/* Crayon sur le bureau - Hidden on mobile */}
          <div className="hidden sm:block absolute bottom-14 sm:bottom-18 md:bottom-24 left-1/2 -translate-x-12 sm:-translate-x-16 md:-translate-x-20">
            <Pencil className="w-12 sm:w-16 md:w-20 h-3 sm:h-4 md:h-5 transform -rotate-12" />
          </div>

          {/* Rangée de chaises vides - RESPONSIVE */}
          <div className="absolute bottom-2 sm:bottom-3 md:bottom-4 left-0 right-0 flex justify-center gap-2 sm:gap-4 md:gap-6 lg:gap-8">
            <Chair className="w-8 sm:w-10 md:w-12 lg:w-16 h-12 sm:h-16 md:h-20 lg:h-24 text-slate-600 transform -rotate-3" />
            <Chair className="w-8 sm:w-10 md:w-12 lg:w-16 h-12 sm:h-16 md:h-20 lg:h-24 text-slate-600 transform rotate-2" />
            <Chair className="w-8 sm:w-10 md:w-12 lg:w-16 h-12 sm:h-16 md:h-20 lg:h-24 text-slate-600 transform -rotate-1" />
            <Chair className="hidden sm:block w-10 md:w-12 lg:w-16 h-16 md:h-20 lg:h-24 text-slate-600 transform rotate-3" />
            <Chair className="hidden md:block w-12 lg:w-16 h-20 lg:h-24 text-slate-600 transform -rotate-2" />
          </div>
          
          {/* Points d'interrogation flottants - RESPONSIVE */}
          <QuestionMark
            className="absolute top-4 sm:top-6 md:top-8 left-8 sm:left-12 md:left-16 text-blue-300"
            size="text-3xl sm:text-4xl md:text-5xl lg:text-6xl"
            duration="2.5s"
          />
          <QuestionMark
            className="absolute top-8 sm:top-12 md:top-16 right-10 sm:right-16 md:right-20 text-indigo-300"
            size="text-2xl sm:text-3xl md:text-4xl lg:text-5xl"
            delay="0.3s"
            duration="3s"
          />
          <QuestionMark
            className="absolute top-2 sm:top-3 md:top-4 left-1/2 -translate-x-1/2 text-cyan-200"
            size="text-2xl sm:text-3xl md:text-4xl"
            delay="0.6s"
            duration="2.8s"
          />
          <QuestionMark
            className="hidden sm:block absolute top-16 sm:top-20 md:top-24 left-16 sm:left-24 md:left-32 text-blue-300"
            size="text-xl sm:text-2xl md:text-3xl"
            delay="0.9s"
            duration="2.2s"
          />
          <QuestionMark
            className="hidden md:block absolute top-12 sm:top-16 md:top-20 right-16 sm:right-24 md:right-32 text-sky-300"
            size="text-2xl sm:text-3xl md:text-4xl"
            delay="1.2s"
            duration="2.6s"
          />
          
          {/* Particules décoratives */}
          <div className="absolute top-12 left-24 w-3 h-3 bg-sky-300 rounded-full animate-ping" style={{animationDuration: '3s'}} />
          <div className="absolute top-28 right-28 w-2 h-2 bg-indigo-300 rounded-full animate-ping" style={{animationDuration: '4s'}} />
          <div className="absolute top-6 right-40 w-2.5 h-2.5 bg-blue-300 rounded-full animate-ping" style={{animationDuration: '3.5s'}} />
          
          {/* Nuages/bulles de pensée */}
          <div className="absolute top-2 left-8 flex gap-1 opacity-30">
            <div className="w-4 h-4 bg-slate-700 rounded-full" />
            <div className="w-6 h-6 bg-slate-700 rounded-full -mt-1" />
            <div className="w-4 h-4 bg-slate-700 rounded-full" />
          </div>
          <div className="absolute top-6 right-12 flex gap-1 opacity-20">
            <div className="w-3 h-3 bg-slate-800 rounded-full" />
            <div className="w-5 h-5 bg-slate-800 rounded-full -mt-1" />
            <div className="w-3 h-3 bg-slate-800 rounded-full" />
          </div>
        </div>

        {/* Texte */}
        <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-slate-800 mb-2">
          La salle de classe est vide !
        </h2>
        <p className="text-sm sm:text-base text-slate-500 mb-2">
          Matière <span className="font-semibold text-blue-600">{matiere}</span> • Classe <span className="font-semibold text-blue-600">{classe}</span>
        </p>

        <p className="text-xs sm:text-sm text-slate-400 mb-4 sm:mb-6">
          En attente des élèves...
        </p>

        {/* Info box */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl sm:rounded-2xl p-3 sm:p-4 mb-4 sm:mb-6 max-w-2xl mx-auto">
          <div className="flex items-start gap-2 sm:gap-3">
            <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg sm:rounded-xl flex-shrink-0">
              <Info size={16} className="sm:w-[18px] sm:h-[18px] text-blue-600" />
            </div>
            <div className="text-left">
              <p className="text-xs sm:text-sm text-slate-600">
                La classe <span className="font-semibold text-slate-800">{classe}</span> existe mais n'a pas encore
                d'élèves. L'import se fait désormais au niveau de l'établissement (fichier complet),
                par le délégué ou le chef.
              </p>
            </div>
          </div>
        </div>

        {/* Texte d'aide */}
        <p className="text-xs sm:text-sm text-slate-500 text-center mb-3 sm:mb-4">
          Les élèves apparaîtront automatiquement dès que :
        </p>

        {/* Options */}
        <div className="space-y-2 sm:space-y-3 max-w-2xl mx-auto">
             {/* CTA — vers le wizard d'import établissement (chef ou délégué) */}
          {peutImporter && (
            <div className="relative">
              {/* Animated border glow effect */}
              <div className="absolute -inset-0.5 bg-gradient-to-r from-amber-400 via-orange-500 to-amber-400 rounded-xl sm:rounded-2xl opacity-75 blur-sm animate-pulse"></div>

              <button
                onClick={() => navigate('/home/import-etablissement')}
                className="relative group w-full p-3 sm:p-4 bg-white border-2 sm:border-3 border-amber-400 hover:border-amber-500 rounded-xl sm:rounded-2xl transition-all duration-300 hover:shadow-xl hover:shadow-amber-200 text-left"
              >
                <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
                  <div className="p-2 sm:p-2.5 md:p-3 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg sm:rounded-xl shadow-lg shadow-amber-500/30 group-hover:scale-110 transition-transform flex-shrink-0">
                    <Upload size={16} className="sm:w-[18px] sm:h-[18px] md:w-[20px] md:h-[20px] text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm sm:text-base md:text-lg lg:text-xl text-slate-800 flex items-center gap-2 flex-wrap">
                      <span className="truncate">Importer les élèves de l'établissement</span>
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-bold rounded-full whitespace-nowrap">Action</span>
                    </p>
                    <p className="text-xs sm:text-sm text-slate-500 hidden sm:block">Ouvre l'import établissement (fichier complet, 5 colonnes)</p>
                  </div>
                  <ChevronRight size={16} className="sm:w-[18px] sm:h-[18px] md:w-[20px] md:h-[20px] text-slate-300 group-hover:text-amber-500 group-hover:translate-x-1 transition-all flex-shrink-0" />
                </div>
              </button>
            </div>
          )}
          {/* Option 1 - Import par le délégué */}
          <div className="w-full p-3 sm:p-4 bg-white border-2 border-emerald-100 rounded-xl sm:rounded-2xl text-left">
            <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
              <div className="p-2 sm:p-2.5 md:p-3 bg-gradient-to-br from-emerald-400 to-green-500 rounded-lg sm:rounded-xl shadow-lg shadow-emerald-500/30 flex-shrink-0">
                <Upload size={16} className="sm:w-[18px] sm:h-[18px] md:w-[20px] md:h-[20px] text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm sm:text-base md:text-lg lg:text-xl text-slate-800">
                  Le délégué (ou le chef) importe l'établissement
                </p>
                <p className="text-xs sm:text-sm text-slate-500 hidden sm:block">
                  {peutImporter ? "Via l'Import établissement (menu de gauche)" : "Demandez à votre délégué ou à votre chef d'établissement"}
                </p>
              </div>
            </div>
          </div>


          {/* Option 2 - Auto-sync élèves */}
          <div className="w-full p-3 sm:p-4 bg-white border-2 border-violet-100 rounded-xl sm:rounded-2xl text-left">
            <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
              <div className="p-2 sm:p-2.5 md:p-3 bg-gradient-to-br from-violet-400 to-indigo-500 rounded-lg sm:rounded-xl shadow-lg shadow-violet-500/30 flex-shrink-0">
                <Wifi size={16} className="sm:w-[18px] sm:h-[18px] md:w-[20px] md:h-[20px] text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm sm:text-base md:text-lg lg:text-xl text-slate-800">Les élèves se connectent eux-mêmes</p>
                <p className="text-xs sm:text-sm text-slate-500 hidden sm:block">Les élèves seront automatiquement synchronisés</p>
              </div>
            </div>
          </div>


        </div>
      </div>
    </div>
  </>
  );
};

export default EmptyClassroom;
